import PageHeader from "../components/ds/PageHeader";
import ViewTabs from "../components/ds/ViewTabs";
import Switch from "../components/ds/Switch";
import { PopUpSelect } from "../components/ds/PopUpButton";
import { useCallback, useEffect, useState } from "react";
import {
  FiLoader,
  FiUsers,
  FiMousePointer,
  FiTrendingUp,
  FiDollarSign,
  FiCheck,
  FiSlash,
  FiRotateCcw,
  FiImage,
  FiLock,
  FiUserPlus,
  FiSearch,
} from "react-icons/fi";
import { useTranslation } from "../context/TranslationContext";
import {
  getMintSpaceAffiliateProgram,
  saveMintSpaceAffiliateProgram,
  getMintSpaceAffiliates,
  updateMintSpaceAffiliate,
  getMintSpaceAffiliateReferrals,
  addMintSpaceAffiliate,
  searchMintSpaceAffiliateCandidates,
} from "../api/affiliate";

const CURRENCY = "₾";
const TABS = ["settings", "affiliates", "conversions"];

/**
 * Owner view of a product's affiliate program: pick the product, turn it on, set the
 * commission, then watch who is promoting it and who they brought in.
 *
 * Programs are per product — a space with three products can run three of them, each with
 * its own rate. The picker at the top decides which one the settings tab is editing.
 */
const MintSpaceAffiliatePage = ({ isDark, setToast, mintSpace, initialProductId }) => {
  const { t } = useTranslation();
  const spaceId = mintSpace?._id;

  const [tab, setTab] = useState("settings");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [program, setProgram] = useState(null);
  const [products, setProducts] = useState([]);
  const [productId, setProductId] = useState("");
  const [form, setForm] = useState({
    enabled: false,
    industryType: "",
    description: "",
    commissionType: "percentage",
    commissionRate: 30,
    commissionAmount: 0,
    recurring: false,
    recurringCycles: 0,
    cookieWindowDays: 30,
    holdDays: 14,
    approvalMode: "auto",
    visibility: "public",
    terms: "",
  });

  const [affiliates, setAffiliates] = useState([]);
  // Adding a specific person — the only way into a private program.
  const [findQuery, setFindQuery] = useState("");
  const [found, setFound] = useState([]);
  const [finding, setFinding] = useState(false);
  const [addingId, setAddingId] = useState(null);
  const [affiliatesLoading, setAffiliatesLoading] = useState(false);
  const [referrals, setReferrals] = useState([]);
  const [referralTotals, setReferralTotals] = useState({ pending: 0, paid: 0 });
  const [referralsLoading, setReferralsLoading] = useState(false);

  const applyProgram = useCallback((data, defaults) => {
    setProgram(data);
    setForm({
      enabled: data?.enabled ?? false,
      industryType: data?.industryType ?? defaults?.industryType ?? "",
      description: data?.description ?? "",
      commissionType: data?.commissionType ?? "percentage",
      commissionRate: data?.commissionRate ?? 30,
      commissionAmount: data?.commissionAmount ?? 0,
      recurring: data?.recurring ?? false,
      recurringCycles: data?.recurringCycles ?? 0,
      cookieWindowDays: data?.cookieWindowDays ?? 30,
      holdDays: data?.holdDays ?? 14,
      approvalMode: data?.approvalMode ?? "auto",
      visibility: data?.visibility ?? "public",
      terms: data?.terms ?? "",
    });
  }, []);

  /** Load one product's program. Empty `pid` lets the server pick the first product. */
  const loadProgram = useCallback(
    async (pid = "") => {
      if (!spaceId) return;
      setLoading(true);
      try {
        const res = await getMintSpaceAffiliateProgram(spaceId, pid);
        if (res?.success) {
          setProducts(res.products || []);
          setProductId(String(res.selectedProductId || ""));
          applyProgram(res.data, res.defaults);
        }
      } catch (err) {
        setToast?.(err?.message || t("affiliate.errorLoading") || "Could not load program");
      } finally {
        setLoading(false);
      }
    },
    [spaceId, applyProgram, setToast, t]
  );

  useEffect(() => {
    loadProgram(initialProductId || "");
  }, [loadProgram, initialProductId]);

  // The affiliate and conversion tabs follow the product picker so the whole page is
  // talking about the same product.
  const loadAffiliates = useCallback(async () => {
    if (!spaceId) return;
    setAffiliatesLoading(true);
    try {
      const res = await getMintSpaceAffiliates(spaceId, productId);
      if (res?.success) setAffiliates(res.data || []);
    } catch (err) {
      setToast?.(err?.message || t("affiliate.errorLoading") || "Could not load affiliates");
    } finally {
      setAffiliatesLoading(false);
    }
  }, [spaceId, productId, setToast, t]);

  const loadReferrals = useCallback(async () => {
    if (!spaceId) return;
    setReferralsLoading(true);
    try {
      const res = await getMintSpaceAffiliateReferrals(spaceId, productId);
      if (res?.success) {
        setReferrals(res.data || []);
        setReferralTotals(res.totals || { pending: 0, paid: 0 });
      }
    } catch (err) {
      setToast?.(err?.message || t("affiliate.errorLoading") || "Could not load conversions");
    } finally {
      setReferralsLoading(false);
    }
  }, [spaceId, productId, setToast, t]);

  useEffect(() => {
    if (tab === "affiliates") loadAffiliates();
    if (tab === "conversions") loadReferrals();
  }, [tab, loadAffiliates, loadReferrals]);

  // People to add, as the owner types; a new product starts the search over.
  useEffect(() => {
    setFindQuery("");
    setFound([]);
  }, [productId]);
  useEffect(() => {
    const q = findQuery.trim();
    if (!spaceId || q.replace(/^@/, "").length < 2) {
      setFound([]);
      setFinding(false);
      return undefined;
    }
    let alive = true;
    setFinding(true);
    const timer = setTimeout(async () => {
      try {
        const res = await searchMintSpaceAffiliateCandidates(spaceId, q, productId);
        if (alive) setFound(res?.data || []);
      } catch {
        if (alive) setFound([]);
      } finally {
        if (alive) setFinding(false);
      }
    }, 250);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [findQuery, spaceId, productId]);

  const addAffiliate = async (person) => {
    setAddingId(person._id);
    try {
      const res = await addMintSpaceAffiliate(spaceId, productId, person._id);
      if (res?.success) {
        setAffiliates((prev) => [res.data, ...prev.filter((a) => a._id !== res.data._id)]);
        setFound((prev) => prev.map((p) => (p._id === person._id ? { ...p, affiliateStatus: "active" } : p)));
        if (!res.alreadyAffiliate) {
          setProgram((p) => (p ? { ...p, stats: { ...p.stats, affiliates: (p.stats?.affiliates || 0) + 1 } } : p));
        }
        setToast?.(t("affiliate.addedToast", { name: person.username }));
      }
    } catch (err) {
      setToast?.(
        err?.code === "program_off" ? t("affiliate.addNeedsProgramOn") : t("affiliate.addFailed")
      );
    } finally {
      setAddingId(null);
    }
  };

  const save = async (overrides = {}) => {
    if (!productId) {
      setToast?.(
        t("affiliate.pickProductFirst") || "Pick a product before saving the program"
      );
      return false;
    }
    setSaving(true);
    try {
      const payload = { ...form, ...overrides, productId };
      const res = await saveMintSpaceAffiliateProgram(spaceId, payload);
      if (res?.success) {
        setProducts(res.products || []);
        applyProgram(res.data);
        setToast?.(t("affiliate.programSaved") || "Affiliate program saved");
      }
      return true;
    } catch (err) {
      setToast?.(err?.message || t("affiliate.errorSaving") || "Could not save program");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const toggleEnabled = async () => {
    const next = !form.enabled;
    const ok = await save({ enabled: next });
    // Reverting on failure keeps the switch honest — a rejected enable (e.g. 0% rate)
    // must not look like it stuck.
    if (!ok) setForm((f) => ({ ...f, enabled: !next }));
    else setForm((f) => ({ ...f, enabled: next }));
  };

  const handleAffiliateAction = async (affiliateId, action) => {
    try {
      const res = await updateMintSpaceAffiliate(spaceId, affiliateId, action);
      if (res?.success) {
        setAffiliates((prev) =>
          prev.map((a) => (a._id === affiliateId ? { ...a, status: res.data.status } : a))
        );
        setToast?.(t("affiliate.affiliateUpdated") || "Affiliate updated");
      }
    } catch (err) {
      setToast?.(err?.message || t("affiliate.errorSaving") || "Could not update affiliate");
    }
  };

  // ----- theme tokens -----
  const baseText = "text-label-primary";
  const muted = "text-label-secondary";
  const card = "border-separator-default bg-bg-raised";
  const inner = "border-separator-default";
  const input = "border-separator-default bg-bg-raised text-label-primary placeholder:text-label-tertiary";

  // Only blank the page on the very first fetch; switching products keeps the picker up.
  if (loading && products.length === 0) {
    return (
      <div className="flex h-40 items-center justify-center">
        <FiLoader className="h-6 w-6 animate-spin text-accent-text" />
      </div>
    );
  }

  const stats = program?.stats;
  const selectedProduct = products.find((p) => String(p._id) === productId) || null;

  const field = (label, help, control) => (
    <div className={`flex flex-wrap items-center justify-between gap-3 rounded-radius-sm border p-3.5 ${inner}`}>
      <div className="min-w-0 flex-1">
        <h3 className={`text-subheadline-semibold ${baseText}`}>{label}</h3>
        {help && <p className={`mt-0.5 text-caption-1 ${muted}`}>{help}</p>}
      </div>
      <div className="shrink-0">{control}</div>
    </div>
  );

  /**
   * Product picker. Every setting below it belongs to whichever product is selected, so it
   * sits above the tabs rather than inside the settings tab.
   *
   * Laid out as cover cards rather than a list — it's the same product the owner sees on
   * Discover, and a banner is far quicker to recognise than a name in a dropdown.
   */
  const renderProductPicker = () => (
    <div className={`mb-4 rounded-radius-sm border p-3.5 ${inner}`}>
      <h3 className={`text-subheadline-semibold ${baseText}`}>
        {t("affiliate.product") || "Product"}
      </h3>
      <p className={`mt-0.5 text-caption-1 ${muted}`}>
        {t("affiliate.productHelp") ||
          "Affiliates promote one product at a time. Each product has its own commission."}
      </p>
      <div className="mt-space-12 grid grid-cols-1 gap-space-8 min-[700px]:grid-cols-2 min-[1200px]:grid-cols-3">
        {products.map((p) => {
          const active = String(p._id) === productId;
          const isFree = p.accessType === "free";
          const cut =
            p.commissionType === "fixed"
              ? `${CURRENCY}${p.commissionAmount}`
              : `${p.commissionRate}%`;
          const state = p.hasProgram
            ? p.programEnabled
              ? t("affiliate.commissionShort").replace("{cut}", cut)
              : t("common.off")
            : t("affiliate.noProgramYet");
          return (
            <button
              key={p._id}
              type="button"
              onClick={() => loadProgram(String(p._id))}
              disabled={loading}
              aria-pressed={active}
              className={`flex min-w-0 items-center gap-space-12 rounded-[22px] p-space-12 text-left outline-none transition-colors focus-visible:shadow-[0_0_0_2px_var(--color-focus-ring)] ${
                active ? "shadow-[inset_0_0_0_1px_var(--color-accent-default)]" : "hover:bg-fill-quaternary"
              }`}
            >
              <span className="relative flex size-12 shrink-0 items-center justify-center overflow-hidden rounded-14 bg-fill-secondary text-label-secondary">
                {p.banner && p.bannerType === "video" ? (
                  <video src={p.banner} className="h-full w-full object-cover" muted playsInline preload="metadata" />
                ) : p.banner ? (
                  <img src={p.banner} alt="" className="h-full w-full object-cover" />
                ) : (
                  <FiImage className="size-5" />
                )}
              </span>
              <span className="flex min-w-0 flex-1 flex-col gap-space-4">
                <span className="truncate text-subheadline-semibold text-label-primary">{p.name}</span>
                <span className="flex min-w-0 items-center gap-space-4 text-footnote text-label-secondary">
                  {p.hasProgram && p.programVisibility === "private" && (
                    <FiLock className="size-3 shrink-0" aria-label={t("affiliate.privateBadge")} />
                  )}
                  {p.hasProgram && p.programVisibility === "members" && (
                    <FiUsers className="size-3 shrink-0" aria-label={t("affiliate.membersOnlyTag")} />
                  )}
                  <span className="truncate">
                    {isFree ? t("affiliate.freeProduct") : `${CURRENCY}${p.price}`} · {state}
                  </span>
                </span>
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );

  const renderNoProducts = () => (
    <p className={`py-12 text-center text-footnote ${muted}`}>
      {t("affiliate.noProducts") ||
        "Add a product under Pricing first — an affiliate program pays out on a product sale."}
    </p>
  );

  const renderSettings = () => (
    <div className="space-y-3">
      {selectedProduct?.accessType === "free" && (
        <div className="rounded-radius-sm border border-status-warning bg-status-warning-bg p-3 text-caption-1 text-status-warning">
          {t("affiliate.freeProductWarning") ||
            "This product is free, so there is no payment to take commission from. Give it a price under Pricing before enabling the program."}
        </div>
      )}

      {field(
        t("affiliate.enableProgram") || "Affiliate program",
        form.enabled
          ? form.visibility === "private"
            ? t("affiliate.enabledPrivateHelp")
            : form.visibility === "members"
              ? t("affiliate.enabledMembersHelp")
              : t("affiliate.enabledHelp") ||
              "Listed in the affiliate marketplace — anyone can grab a referral link."
          : t("affiliate.disabledHelp") ||
              "Turn on to let others promote this product for a cut of each sale.",
        <Switch on={form.enabled} disabled={saving} onClick={toggleEnabled} label={form.enabled ? "Disable program" : "Enable program"} />
      )}

      {field(
        t("affiliate.visibility"),
        t(
          {
            private: "affiliate.visibilityPrivateHelp",
            members: "affiliate.visibilityMembersHelp",
          }[form.visibility] || "affiliate.visibilityPublicHelp"
        ),
        <PopUpSelect
          label={t("affiliate.visibility") || "Visibility"}
          value={form.visibility}
          onChange={(visibility) => setForm((f) => ({ ...f, visibility }))}
          className="self-start"
        >
          <option value="public">{t("affiliate.visibilityPublic")}</option>
          <option value="members">{t("affiliate.visibilityMembers")}</option>
          <option value="private">{t("affiliate.visibilityPrivate")}</option>
        </PopUpSelect>
      )}

      {field(
        t("affiliate.commissionRate") || "Commission rate",
        // Spell the payout out in money — a percentage alone is easy to misjudge.
        selectedProduct && selectedProduct.accessType !== "free" && selectedProduct.price > 0
          ? (t("affiliate.commissionRatePreview") ||
              "The affiliate keeps {cut} of each {price} sale.")
              .replace(
                "{cut}",
                form.commissionType === "percentage"
                  ? `${CURRENCY}${(
                      (selectedProduct.price * (Number(form.commissionRate) || 0)) /
                      100
                    ).toFixed(2)}`
                  : `${CURRENCY}${Number(form.commissionAmount || 0).toFixed(2)}`
              )
              .replace("{price}", `${CURRENCY}${selectedProduct.price}`)
          : t("affiliate.commissionRateHelp") ||
            "What the affiliate keeps from each payment their referral makes.",
        <div className="flex items-center gap-2">
          <PopUpSelect
            label={t("affiliate.commissionRate") || "Commission rate"}
            value={form.commissionType}
            onChange={(commissionType) => setForm((f) => ({ ...f, commissionType }))}
            className="shrink-0"
          >
            <option value="percentage">%</option>
            <option value="fixed">{CURRENCY}</option>
          </PopUpSelect>
          {form.commissionType === "percentage" ? (
            <input
              type="number"
              min="0"
              max="100"
              value={form.commissionRate}
              onChange={(e) => setForm((f) => ({ ...f, commissionRate: e.target.value }))}
              className={`w-20 rounded-radius-xs border px-2 py-1.5 text-right text-footnote outline-none ${input}`}
            />
          ) : (
            <input
              type="number"
              min="0"
              value={form.commissionAmount}
              onChange={(e) => setForm((f) => ({ ...f, commissionAmount: e.target.value }))}
              className={`w-24 rounded-radius-xs border px-2 py-1.5 text-right text-footnote outline-none ${input}`}
            />
          )}
        </div>
      )}

      {field(
        t("affiliate.industryType") || "Industry type",
        t("affiliate.industryTypeHelp") || "Shown in the marketplace table and used by the filter.",
        <input
          type="text"
          value={form.industryType}
          onChange={(e) => setForm((f) => ({ ...f, industryType: e.target.value }))}
          placeholder={mintSpace?.category || ""}
          className={`w-48 rounded-radius-xs border px-2.5 py-1.5 text-footnote outline-none ${input}`}
        />
      )}

      {field(
        t("affiliate.recurring") || "Pay on renewals",
        t("affiliate.recurringHelp") ||
          "Off means the affiliate only earns on the referral's first payment.",
        <Switch on={form.recurring} onClick={() => setForm((f) => ({ ...f, recurring: !f.recurring }))} label="Toggle recurring commission" />
      )}

      {field(
        t("affiliate.cookieWindowLabel") || "Attribution window",
        t("affiliate.cookieWindowHelp") ||
          "How long after clicking a link a join still counts for the affiliate.",
        <div className="flex items-center gap-1.5">
          <input
            type="number"
            min="1"
            max="365"
            value={form.cookieWindowDays}
            onChange={(e) => setForm((f) => ({ ...f, cookieWindowDays: e.target.value }))}
            className={`w-20 rounded-radius-xs border px-2 py-1.5 text-right text-footnote outline-none ${input}`}
          />
          <span className={`text-caption-1 ${muted}`}>{t("affiliate.days") || "days"}</span>
        </div>
      )}

      {field(
        t("affiliate.holdPeriod") || "Hold period",
        t("affiliate.holdPeriodHelp") ||
          "Days a commission stays on hold before it becomes withdrawable.",
        <div className="flex items-center gap-1.5">
          <input
            type="number"
            min="0"
            max="180"
            value={form.holdDays}
            onChange={(e) => setForm((f) => ({ ...f, holdDays: e.target.value }))}
            className={`w-20 rounded-radius-xs border px-2 py-1.5 text-right text-footnote outline-none ${input}`}
          />
          <span className={`text-caption-1 ${muted}`}>{t("affiliate.days") || "days"}</span>
        </div>
      )}

      {form.visibility !== "private" && field(
        t("affiliate.approvalMode") || "Approval",
        t("affiliate.approvalModeHelp") || "Review each affiliate before their link goes live.",
        <PopUpSelect
          label={t("affiliate.approvalMode") || "Approval"}
          value={form.approvalMode}
          onChange={(approvalMode) => setForm((f) => ({ ...f, approvalMode }))}
          className="self-start"
        >
          <option value="auto">{t("affiliate.approvalAuto") || "Instant"}</option>
          <option value="manual">{t("affiliate.approvalManual") || "Manual review"}</option>
        </PopUpSelect>
      )}

      <div className={`rounded-radius-sm border p-3.5 ${inner}`}>
        <h3 className={`text-subheadline-semibold ${baseText}`}>
          {t("affiliate.pitch") || "Pitch"}
        </h3>
        <p className={`mt-0.5 text-caption-1 ${muted}`}>
          {t("affiliate.pitchHelp") || "One or two lines telling affiliates why to promote you."}
        </p>
        <textarea
          rows={2}
          value={form.description}
          onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
          className={`mt-2 w-full resize-y rounded-radius-xs border px-3 py-2 text-footnote outline-none ${input}`}
        />
      </div>

      <div className={`rounded-radius-sm border p-3.5 ${inner}`}>
        <h3 className={`text-subheadline-semibold ${baseText}`}>
          {t("affiliate.terms") || "Terms"}
        </h3>
        <p className={`mt-0.5 text-caption-1 ${muted}`}>
          {t("affiliate.termsHelp") || "Rules affiliates agree to — shown before they get a link."}
        </p>
        <textarea
          rows={3}
          value={form.terms}
          onChange={(e) => setForm((f) => ({ ...f, terms: e.target.value }))}
          className={`mt-2 w-full resize-y rounded-radius-xs border px-3 py-2 text-footnote outline-none ${input}`}
        />
      </div>

      <button
        type="button"
        onClick={() => save()}
        disabled={saving}
        className="w-full rounded-radius-sm bg-accent-default py-2.5 text-footnote-semibold text-accent-on transition hover:opacity-90 disabled:opacity-60 sm:w-auto sm:px-6"
      >
        {saving ? (
          <span className="inline-flex items-center justify-center gap-2">
            <FiLoader className="h-4 w-4 animate-spin" />
            {t("common.saving") || "Saving…"}
          </span>
        ) : (
          t("common.save") || "Save"
        )}
      </button>
    </div>
  );

  /** Search for a person and make them an affiliate of the selected product. */
  const renderAddAffiliate = () => {
    const query = findQuery.trim().replace(/^@/, "");
    return (
      <div className={`mb-3 rounded-radius-sm border p-3.5 ${inner}`}>
        <h3 className={`flex items-center gap-1.5 text-subheadline-semibold ${baseText}`}>
          <FiUserPlus className="h-4 w-4 text-accent-text" />
          {t("affiliate.addTitle")}
        </h3>
        <p className={`mt-0.5 text-caption-1 ${muted}`}>
          {!program?.enabled
            ? t("affiliate.addNeedsProgramOn")
            : program.visibility === "private"
              ? t("affiliate.addHelpPrivate")
              : t("affiliate.addHelp")}
        </p>
        <div className="relative mt-2.5">
          <FiSearch className={`pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 ${muted}`} />
          <input
            type="text"
            value={findQuery}
            onChange={(e) => setFindQuery(e.target.value)}
            disabled={!program?.enabled}
            placeholder={t("affiliate.addPlaceholder")}
            className={`w-full rounded-radius-xs border py-2 pl-8 pr-8 text-footnote outline-none disabled:opacity-50 ${input}`}
          />
          {finding && (
            <FiLoader className="absolute right-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 animate-spin text-accent-text" />
          )}
        </div>
        {found.length > 0 && (
          <ul className="mt-2 space-y-1">
            {found.map((person) => (
              <li
                key={person._id}
                className={`flex items-center gap-2.5 rounded-radius-xs px-2 py-1.5 hover:bg-fill-quaternary`}
              >
                <div className="flex h-7 w-7 shrink-0 items-center justify-center overflow-hidden rounded-full bg-slate-500 text-caption-1-semibold text-white">
                  {person.avatar ? (
                    <img src={person.avatar} alt="" className="h-full w-full object-cover" />
                  ) : (
                    (person.username || "?").charAt(0).toUpperCase()
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className={`truncate text-footnote-semibold ${baseText}`}>@{person.username}</p>
                  {person.businessName && (
                    <p className={`truncate text-caption-1 ${muted}`}>{person.businessName}</p>
                  )}
                </div>
                {person.affiliateStatus === "active" ? (
                  <span className="shrink-0 text-caption-1-semibold text-accent-text">
                    {t("affiliate.alreadyAffiliate")}
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => addAffiliate(person)}
                    disabled={addingId === person._id}
                    className="inline-flex shrink-0 items-center gap-1 rounded-radius-xs bg-accent-default px-2.5 py-1 text-caption-1-semibold text-accent-on transition hover:opacity-90 disabled:opacity-60"
                  >
                    {addingId === person._id ? (
                      <FiLoader className="h-3 w-3 animate-spin" />
                    ) : (
                      <FiUserPlus className="h-3 w-3" />
                    )}
                    {t("affiliate.add")}
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
        {!finding && query.length >= 2 && found.length === 0 && (
          <p className={`mt-2 text-caption-1 ${muted}`}>{t("affiliate.addNoMatches")}</p>
        )}
      </div>
    );
  };

  const renderAffiliates = () => (
    <>
      {program && renderAddAffiliate()}
      {renderAffiliateList()}
    </>
  );

  const renderAffiliateList = () => {
    if (affiliatesLoading) {
      return (
        <div className="flex h-32 items-center justify-center">
          <FiLoader className="h-6 w-6 animate-spin text-accent-text" />
        </div>
      );
    }
    if (affiliates.length === 0) {
      return (
        <p className={`py-12 text-center text-footnote ${muted}`}>
          {program?.visibility === "private"
            ? t("affiliate.noAffiliatesPrivate")
            : t("affiliate.noAffiliatesYet") ||
              "No one is promoting this mint space yet. Enable the program to appear in the marketplace."}
        </p>
      );
    }
    return (
      <div className="space-y-2">
        {affiliates.map((a) => (
          <div key={a._id} className={`rounded-radius-sm border p-3 ${inner}`}>
            <div className="flex items-center gap-2.5">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full bg-slate-500 text-caption-1-semibold text-white">
                {a.avatar ? (
                  <img src={a.avatar} alt="" className="h-full w-full object-cover" />
                ) : (
                  (a.name || "?").charAt(0).toUpperCase()
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p className={`truncate text-footnote-semibold ${baseText}`}>{a.name}</p>
                <p className={`truncate font-mono text-caption-1 ${muted}`}>{a.code}</p>
              </div>
              {a.addedByOwner && (
                <span className={`shrink-0 rounded-full px-2 py-0.5 text-caption-1-semibold bg-fill-tertiary text-label-secondary`}>
                  {t("affiliate.addedByYou")}
                </span>
              )}
              <span
                className={`shrink-0 rounded-full px-2 py-0.5 text-caption-1-semibold ${
                  a.status === "active"
                    ? "bg-accent-tinted text-accent-text"
                    : a.status === "pending"
                      ? "bg-status-warning-bg text-status-warning"
                      : "bg-status-danger-bg text-status-danger"
                }`}
              >
                {a.status}
              </span>
            </div>

            <div className={`mt-2.5 grid grid-cols-4 gap-2 border-t pt-2.5 text-caption-1 ${inner}`}>
              <div>
                <p className={muted}>{t("affiliate.clicks") || "Clicks"}</p>
                <p className="font-semibold">{a.clicks.toLocaleString()}</p>
              </div>
              <div>
                <p className={muted}>{t("affiliate.conversions") || "Conversions"}</p>
                <p className="font-semibold">{a.conversions.toLocaleString()}</p>
              </div>
              <div>
                <p className={muted}>{t("affiliate.pendingEarnings") || "Pending"}</p>
                <p className="font-semibold">
                  {CURRENCY}
                  {a.pendingEarnings.toFixed(2)}
                </p>
              </div>
              <div>
                <p className={muted}>{t("affiliate.earnings") || "Earned"}</p>
                <p className="font-semibold text-accent-text">
                  {CURRENCY}
                  {a.earnings.toFixed(2)}
                </p>
              </div>
            </div>

            <div className="mt-2.5 flex flex-wrap gap-2">
              {a.status === "pending" && (
                <>
                  <button
                    type="button"
                    onClick={() => handleAffiliateAction(a._id, "approve")}
                    className="inline-flex items-center gap-1.5 rounded-radius-xs bg-accent-tinted px-2.5 py-1.5 text-caption-1-semibold text-accent-text transition hover:bg-accent-tinted"
                  >
                    <FiCheck className="h-3 w-3" />
                    {t("affiliate.approve") || "Approve"}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleAffiliateAction(a._id, "reject")}
                    className="inline-flex items-center gap-1.5 rounded-radius-xs bg-status-danger-bg px-2.5 py-1.5 text-caption-1-semibold text-status-danger transition hover:bg-status-danger-bg"
                  >
                    <FiSlash className="h-3 w-3" />
                    {t("affiliate.reject") || "Reject"}
                  </button>
                </>
              )}
              {a.status === "active" && (
                <button
                  type="button"
                  onClick={() => handleAffiliateAction(a._id, "disable")}
                  className={`inline-flex items-center gap-1.5 rounded-radius-xs border px-2.5 py-1.5 text-caption-1-semibold transition ${inner} ${
                    "hover:bg-fill-quaternary"
                  }`}
                >
                  <FiSlash className="h-3 w-3" />
                  {t("affiliate.disable") || "Disable"}
                </button>
              )}
              {(a.status === "disabled" || a.status === "rejected") && (
                <button
                  type="button"
                  onClick={() => handleAffiliateAction(a._id, "enable")}
                  className={`inline-flex items-center gap-1.5 rounded-radius-xs border px-2.5 py-1.5 text-caption-1-semibold transition ${inner} ${
                    "hover:bg-fill-quaternary"
                  }`}
                >
                  <FiRotateCcw className="h-3 w-3" />
                  {t("affiliate.reinstate") || "Re-enable"}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderConversions = () => {
    if (referralsLoading) {
      return (
        <div className="flex h-32 items-center justify-center">
          <FiLoader className="h-6 w-6 animate-spin text-accent-text" />
        </div>
      );
    }
    return (
      <>
        <div className="mb-3 grid grid-cols-2 gap-2">
          <div className={`rounded-radius-sm border p-3 ${inner}`}>
            <p className={`text-caption-1 uppercase tracking-wide ${muted}`}>
              {t("affiliate.commissionPending") || "Commission on hold"}
            </p>
            <p className="mt-0.5 text-headline">
              {CURRENCY}
              {referralTotals.pending.toFixed(2)}
            </p>
          </div>
          <div className={`rounded-radius-sm border p-3 ${inner}`}>
            <p className={`text-caption-1 uppercase tracking-wide ${muted}`}>
              {t("affiliate.commissionPaid") || "Commission paid"}
            </p>
            <p className="mt-0.5 text-headline text-accent-text">
              {CURRENCY}
              {referralTotals.paid.toFixed(2)}
            </p>
          </div>
        </div>

        {referrals.length === 0 ? (
          <p className={`py-12 text-center text-footnote ${muted}`}>
            {t("affiliate.noConversionsYet") || "No referred members yet."}
          </p>
        ) : (
          <div className="space-y-2">
            {referrals.map((r) => (
              <div key={r._id} className={`flex items-center gap-2.5 rounded-radius-sm border p-3 ${inner}`}>
                <div className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full bg-slate-500 text-caption-1-semibold text-white">
                  {r.memberAvatar ? (
                    <img src={r.memberAvatar} alt="" className="h-full w-full object-cover" />
                  ) : (
                    (r.member || "?").charAt(0).toUpperCase()
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className={`truncate text-footnote-semibold ${baseText}`}>{r.member}</p>
                  <p className={`truncate text-caption-1 ${muted}`}>
                    {(t("affiliate.referredBy") || "Referred by {name}").replace(
                      "{name}",
                      r.affiliateName
                    )}
                  </p>
                </div>
                <div className="shrink-0 text-right">
                  <p className="text-footnote-semibold">
                    {CURRENCY}
                    {(r.totalCommission || 0).toFixed(2)}
                  </p>
                  <p className={`text-caption-1 ${muted}`}>{r.status}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </>
    );
  };

  return (
    <div className="flex w-full flex-col gap-space-24 p-space-8 lg:p-0">
      <PageHeader
        title={t("affiliate.ownerTitle") || "Affiliate program"}
        subtitle={
          t("affiliate.ownerSubtitle") || "Let other people promote one of your products and earn a cut of every sale."
        }
      />
      <div className={`rounded-radius-lg border p-space-16 ${card}`}>
        {products.length > 0 && renderProductPicker()}

        {program && (
          <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
            {[
              { icon: FiUsers, label: t("affiliate.affiliates") || "Affiliates", value: stats.affiliates },
              { icon: FiMousePointer, label: t("affiliate.clicks") || "Clicks", value: stats.clicks },
              {
                icon: FiTrendingUp,
                label: t("affiliate.conversions") || "Conversions",
                value: stats.conversions,
              },
              {
                icon: FiDollarSign,
                label: t("affiliate.affiliateEarnings") || "Paid out",
                value: `${CURRENCY}${stats.affiliateEarnings.toFixed(2)}`,
              },
            ].map((s) => (
              <div key={s.label} className={`rounded-radius-sm border p-3 ${inner}`}>
                <div className={`flex items-center gap-1.5 text-caption-1 uppercase tracking-wide ${muted}`}>
                  <s.icon className="h-3 w-3" />
                  {s.label}
                </div>
                <p className={`mt-0.5 text-headline ${baseText}`}>{s.value}</p>
              </div>
            ))}
          </div>
        )}

        <div className="mb-space-16">
          <ViewTabs
            label={t("affiliate.tab_settings")}
            value={tab}
            onChange={setTab}
            options={TABS.map((id) => ({ value: id, label: t(`affiliate.tab_${id}`) }))}
          />
        </div>

        {products.length === 0 ? (
          renderNoProducts()
        ) : loading ? (
          <div className="flex h-40 items-center justify-center">
            <FiLoader className="h-6 w-6 animate-spin text-accent-text" />
          </div>
        ) : (
          <>
            {tab === "settings" && renderSettings()}
            {tab === "affiliates" && renderAffiliates()}
            {tab === "conversions" && renderConversions()}
          </>
        )}
      </div>
    </div>
  );
};

export default MintSpaceAffiliatePage;
